import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Comprehensive emoji data with names for search
const EMOJI_DATA: Record<string, { emoji: string; name: string }[]> = {
  'Smileys': [
    { emoji: '😀', name: 'grinning face' },
    { emoji: '😃', name: 'grinning face big eyes' },
    { emoji: '😄', name: 'grinning squinting face' },
    { emoji: '😁', name: 'beaming face' },
    { emoji: '😅', name: 'grinning sweat' },
    { emoji: '🤣', name: 'rolling laughing' },
    { emoji: '😂', name: 'joy tears' },
    { emoji: '🙂', name: 'slightly smiling' },
    { emoji: '😉', name: 'winking face' },
    { emoji: '😊', name: 'smiling blush' },
    { emoji: '😇', name: 'smiling halo angel' },
    { emoji: '🥰', name: 'smiling hearts love' },
    { emoji: '😍', name: 'heart eyes' },
    { emoji: '🤩', name: 'star struck' },
    { emoji: '😘', name: 'kissing heart' },
    { emoji: '😋', name: 'yum delicious' },
    { emoji: '😛', name: 'tongue out' },
    { emoji: '😜', name: 'winking tongue' },
    { emoji: '🤪', name: 'zany crazy' },
    { emoji: '🤑', name: 'money face rich' },
    { emoji: '🤗', name: 'hugging face' },
    { emoji: '🤭', name: 'hand over mouth' },
    { emoji: '🤫', name: 'shushing quiet' },
    { emoji: '🤔', name: 'thinking hmm' },
    { emoji: '😏', name: 'smirking face' },
    { emoji: '😒', name: 'unamused' },
    { emoji: '🙄', name: 'eye roll' },
    { emoji: '😬', name: 'grimacing awkward' },
    { emoji: '😌', name: 'relieved peaceful' },
    { emoji: '😴', name: 'sleeping zzz' },
    { emoji: '🤯', name: 'mind blown exploding' },
    { emoji: '🥳', name: 'party celebration' },
    { emoji: '😎', name: 'cool sunglasses' },
    { emoji: '🤓', name: 'nerd glasses' },
    { emoji: '😕', name: 'confused' },
    { emoji: '😟', name: 'worried' },
    { emoji: '😮', name: 'open mouth surprised' },
    { emoji: '😲', name: 'astonished shocked' },
    { emoji: '😳', name: 'flushed embarrassed' },
    { emoji: '🥺', name: 'pleading puppy eyes' },
    { emoji: '😢', name: 'crying tear' },
    { emoji: '😭', name: 'loudly crying sobbing' },
    { emoji: '😱', name: 'screaming fear' },
    { emoji: '😤', name: 'huffing angry' },
    { emoji: '😡', name: 'pouting angry red' },
    { emoji: '🤬', name: 'cursing swearing' },
    { emoji: '😈', name: 'smiling devil' },
    { emoji: '💀', name: 'skull dead' },
    { emoji: '💩', name: 'poop poo' },
    { emoji: '🤡', name: 'clown' },
    { emoji: '👻', name: 'ghost boo' },
    { emoji: '👽', name: 'alien' },
    { emoji: '🤖', name: 'robot' },
  ],
  'Gestures': [
    { emoji: '👋', name: 'waving hand hello bye' },
    { emoji: '👌', name: 'ok hand perfect' },
    { emoji: '🤌', name: 'pinched fingers italian' },
    { emoji: '✌️', name: 'victory peace' },
    { emoji: '🤞', name: 'crossed fingers luck' },
    { emoji: '🤟', name: 'love you gesture' },
    { emoji: '🤘', name: 'rock on horns' },
    { emoji: '🤙', name: 'call me hand' },
    { emoji: '👈', name: 'pointing left' },
    { emoji: '👉', name: 'pointing right' },
    { emoji: '👆', name: 'pointing up' },
    { emoji: '👇', name: 'pointing down' },
    { emoji: '👍', name: 'thumbs up like' },
    { emoji: '👎', name: 'thumbs down dislike' },
    { emoji: '✊', name: 'raised fist' },
    { emoji: '👊', name: 'fist bump punch' },
    { emoji: '👏', name: 'clapping hands applause' },
    { emoji: '🙌', name: 'raising hands celebrate' },
    { emoji: '🤝', name: 'handshake deal' },
    { emoji: '🙏', name: 'folded hands pray thanks' },
    { emoji: '💪', name: 'flexed biceps strong' },
    { emoji: '👀', name: 'eyes look see' },
    { emoji: '🧠', name: 'brain think' },
  ],
  'Hearts': [
    { emoji: '❤️', name: 'red heart love' },
    { emoji: '🧡', name: 'orange heart' },
    { emoji: '💛', name: 'yellow heart' },
    { emoji: '💚', name: 'green heart' },
    { emoji: '💙', name: 'blue heart' },
    { emoji: '💜', name: 'purple heart' },
    { emoji: '🖤', name: 'black heart' },
    { emoji: '🤍', name: 'white heart' },
    { emoji: '💔', name: 'broken heart' },
    { emoji: '❤️‍🔥', name: 'heart on fire' },
    { emoji: '💕', name: 'two hearts' },
    { emoji: '💖', name: 'sparkling heart' },
    { emoji: '💝', name: 'heart ribbon gift' },
  ],
  'Finance': [
    { emoji: '📈', name: 'chart increasing up bull green' },
    { emoji: '📉', name: 'chart decreasing down bear red' },
    { emoji: '💰', name: 'money bag rich' },
    { emoji: '💵', name: 'dollar bill cash' },
    { emoji: '💸', name: 'money wings flying' },
    { emoji: '💳', name: 'credit card' },
    { emoji: '🏦', name: 'bank building' },
    { emoji: '💹', name: 'chart yen rising stocks' },
    { emoji: '📊', name: 'bar chart statistics' },
    { emoji: '🪙', name: 'coin crypto' },
    { emoji: '💎', name: 'gem diamond hands' },
    { emoji: '⚖️', name: 'balance scale' },
    { emoji: '💲', name: 'dollar sign' },
    { emoji: '🧾', name: 'receipt' },
    { emoji: '🔐', name: 'locked key secure' },
  ],
  'Symbols': [
    { emoji: '⭐', name: 'star' },
    { emoji: '🌟', name: 'glowing star' },
    { emoji: '✨', name: 'sparkles' },
    { emoji: '⚡', name: 'lightning zap' },
    { emoji: '🔥', name: 'fire hot lit' },
    { emoji: '💯', name: 'hundred percent perfect' },
    { emoji: '✅', name: 'check mark done' },
    { emoji: '❌', name: 'cross mark no' },
    { emoji: '❓', name: 'question mark' },
    { emoji: '❗', name: 'exclamation mark' },
    { emoji: '⚠️', name: 'warning caution' },
    { emoji: '🚨', name: 'police light alert' },
    { emoji: '💡', name: 'light bulb idea' },
    { emoji: '📌', name: 'pushpin pin' },
    { emoji: '🎯', name: 'bullseye target' },
    { emoji: '🏆', name: 'trophy winner' },
    { emoji: '🥇', name: 'first place gold' },
    { emoji: '🎉', name: 'party popper celebration' },
    { emoji: '🔔', name: 'bell notification' },
    { emoji: '⏰', name: 'alarm clock time' },
    { emoji: '📅', name: 'calendar date' },
    { emoji: '📝', name: 'memo note' },
    { emoji: '🔗', name: 'link chain url' },
    { emoji: '🔴', name: 'red circle' },
    { emoji: '🟢', name: 'green circle' },
    { emoji: '🔵', name: 'blue circle' },
    { emoji: '🟡', name: 'yellow circle' },
    { emoji: '➕', name: 'plus add' },
    { emoji: '➖', name: 'minus subtract' },
    { emoji: '💬', name: 'speech bubble chat' },
  ],
  'Animals': [
    { emoji: '🐂', name: 'ox bull market' },
    { emoji: '🐻', name: 'bear face market' },
    { emoji: '🦬', name: 'bison bull' },
    { emoji: '🐃', name: 'water buffalo bull' },
    { emoji: '🐶', name: 'dog face' },
    { emoji: '🐱', name: 'cat face' },
    { emoji: '🦊', name: 'fox face' },
    { emoji: '🦁', name: 'lion face' },
    { emoji: '🐯', name: 'tiger face' },
    { emoji: '🐵', name: 'monkey face ape' },
    { emoji: '🦍', name: 'gorilla ape' },
    { emoji: '🐔', name: 'chicken' },
    { emoji: '🦅', name: 'eagle' },
    { emoji: '🦈', name: 'shark' },
    { emoji: '🐳', name: 'whale' },
    { emoji: '🐙', name: 'octopus' },
    { emoji: '🦋', name: 'butterfly' },
    { emoji: '🐝', name: 'bee honeybee' },
    { emoji: '🚀', name: 'rocket moon' },
    { emoji: '🌙', name: 'moon' },
  ],
  'Food': [
    { emoji: '🍎', name: 'red apple' },
    { emoji: '🍕', name: 'pizza' },
    { emoji: '🍔', name: 'hamburger burger' },
    { emoji: '🌮', name: 'taco' },
    { emoji: '🍟', name: 'french fries' },
    { emoji: '🍿', name: 'popcorn' },
    { emoji: '🍩', name: 'doughnut donut' },
    { emoji: '🍪', name: 'cookie' },
    { emoji: '🎂', name: 'birthday cake' },
    { emoji: '🍫', name: 'chocolate bar' },
    { emoji: '☕', name: 'hot beverage coffee' },
    { emoji: '🍺', name: 'beer mug' },
    { emoji: '🍻', name: 'clinking beer mugs cheers' },
    { emoji: '🥂', name: 'clinking glasses toast champagne' },
    { emoji: '🍾', name: 'bottle popping cork champagne' },
    { emoji: '🥃', name: 'tumbler glass whiskey' },
  ],
};

const STORAGE_KEY = 'emoji-picker-data';
const MAX_RECENT = 16;
const MAX_FAVORITES = 32;

interface StoredData {
  recent: string[];
  favorites: string[];
}

// Load stored data
const loadStoredData = (): StoredData => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore
  }
  return { recent: [], favorites: [] };
};

// Save stored data
const saveStoredData = (data: StoredData) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore
  }
};

// Hook to get and update emoji data
export const useEmojiData = () => {
  const [data, setData] = useState<StoredData>(loadStoredData);

  const addRecent = useCallback((emoji: string) => {
    setData((prev) => {
      const newRecent = [emoji, ...prev.recent.filter((e) => e !== emoji)].slice(0, MAX_RECENT);
      const newData = { ...prev, recent: newRecent };
      saveStoredData(newData);
      return newData;
    });
  }, []);

  const toggleFavorite = useCallback((emoji: string) => {
    setData((prev) => {
      const isFavorite = prev.favorites.includes(emoji);
      const newFavorites = isFavorite
        ? prev.favorites.filter((f) => f !== emoji)
        : [emoji, ...prev.favorites].slice(0, MAX_FAVORITES);
      const newData = { ...prev, favorites: newFavorites };
      saveStoredData(newData);
      return newData;
    });
  }, []);

  return { ...data, addRecent, toggleFavorite };
};

// Inline recent emojis bar - shows recent emojis for quick access
interface RecentEmojisBarProps {
  onSelect: (emoji: string) => void;
  onOpenPicker: () => void;
}

export const RecentEmojisBar: React.FC<RecentEmojisBarProps> = ({ onSelect, onOpenPicker }) => {
  const { recent, addRecent } = useEmojiData();

  const handleSelect = (emoji: string) => {
    addRecent(emoji);
    onSelect(emoji);
  };

  // Default emojis if no recent history
  const defaultEmojis = ['📈', '📉', '🔥', '💯', '✅', '❌', '🎯', '🚀'];
  const displayEmojis = recent.length > 0 ? recent.slice(0, 8) : defaultEmojis;

  return (
    <div className="flex items-center gap-1">
      {displayEmojis.map((emoji, i) => (
        <button
          key={`${emoji}-${i}`}
          onClick={() => handleSelect(emoji)}
          className="text-base hover:bg-dark-surface-light rounded p-0.5 transition-colors"
          title={emoji}
        >
          {emoji}
        </button>
      ))}
      <button
        onClick={onOpenPicker}
        className="text-xs text-gray-500 hover:text-gray-300 px-1.5 py-0.5 hover:bg-dark-surface-light rounded transition-colors"
        title="More emojis"
      >
        more
      </button>
    </div>
  );
};

// Full emoji picker modal
interface EmojiPickerModalProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

export const EmojiPickerModal: React.FC<EmojiPickerModalProps> = ({ onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Smileys');
  const { recent, favorites, addRecent, toggleFavorite } = useEmojiData();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search on mount
  useEffect(() => {
    setTimeout(() => searchInputRef.current?.focus(), 50);
  }, []);

  // Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Build categories with recent/favorites
  const categories = useMemo(() => {
    const result: Record<string, { emoji: string; name: string }[]> = {};

    if (recent.length > 0) {
      result['Recent'] = recent.map((e) => ({ emoji: e, name: 'recent' }));
    }
    if (favorites.length > 0) {
      result['Favorites'] = favorites.map((e) => ({ emoji: e, name: 'favorite' }));
    }

    Object.entries(EMOJI_DATA).forEach(([cat, emojis]) => {
      result[cat] = emojis;
    });

    return result;
  }, [recent, favorites]);

  // Filter by search
  const filteredEmojis = useMemo(() => {
    if (!search.trim()) {
      return categories[activeCategory] || [];
    }

    const query = search.toLowerCase().trim();
    const results: { emoji: string; name: string }[] = [];
    const seen = new Set<string>();

    Object.values(EMOJI_DATA).forEach((emojis) => {
      emojis.forEach((item) => {
        if (!seen.has(item.emoji) && item.name.toLowerCase().includes(query)) {
          seen.add(item.emoji);
          results.push(item);
        }
      });
    });

    return results;
  }, [search, activeCategory, categories]);

  const handleSelect = useCallback(
    (emoji: string) => {
      addRecent(emoji);
      onSelect(emoji);
    },
    [addRecent, onSelect]
  );

  const handleToggleFavorite = useCallback(
    (emoji: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toggleFavorite(emoji);
    },
    [toggleFavorite]
  );

  const categoryKeys = Object.keys(categories);

  // Modal rendered via portal
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-dark-surface border border-dark-border rounded-lg shadow-2xl overflow-hidden"
        style={{ width: 360, height: 400 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-dark-surface-light border-b border-dark-border">
          <span className="text-sm font-medium text-gray-200">Emojis</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Search */}
        <div className="p-2 border-b border-dark-border">
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search emojis... (e.g. bull, fire, rocket)"
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-[#5865F2]"
          />
        </div>

        {/* Category tabs */}
        {!search && (
          <div className="flex gap-1 px-2 py-1.5 border-b border-dark-border overflow-x-auto">
            {categoryKeys.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-2 py-1 text-xs rounded whitespace-nowrap transition-colors ${
                  activeCategory === cat
                    ? 'bg-[#5865F2] text-white'
                    : 'text-gray-400 hover:text-white hover:bg-dark-surface-light'
                }`}
              >
                {cat === 'Recent' ? '🕐' : cat === 'Favorites' ? '⭐' : cat}
              </button>
            ))}
          </div>
        )}

        {/* Emoji grid */}
        <div className="p-2 overflow-y-auto" style={{ height: search ? 280 : 245 }}>
          {filteredEmojis.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
              {search ? (
                'No emojis found'
              ) : activeCategory === 'Recent' ? (
                <>
                  <span>No recent emojis</span>
                  <span className="text-xs mt-1">Use emojis to add them here</span>
                </>
              ) : (
                <>
                  <span>No favorites yet</span>
                  <span className="text-xs mt-1">Right-click emojis to favorite</span>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-9 gap-0.5">
              {filteredEmojis.map((item, i) => {
                const isFavorite = favorites.includes(item.emoji);
                return (
                  <button
                    key={`${item.emoji}-${i}`}
                    onClick={() => handleSelect(item.emoji)}
                    onContextMenu={(e) => handleToggleFavorite(item.emoji, e)}
                    className={`relative text-xl p-1.5 rounded hover:bg-dark-surface-light transition-colors ${
                      isFavorite ? 'ring-1 ring-accent-yellow/40' : ''
                    }`}
                    title={`${item.name}\nRight-click to ${isFavorite ? 'unfavorite' : 'favorite'}`}
                  >
                    {item.emoji}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 py-1.5 border-t border-dark-border text-[10px] text-gray-500 text-center">
          Click to insert • Right-click to favorite
        </div>
      </div>
    </div>,
    document.body
  );
};

// Legacy export for backwards compatibility
interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect, onClose }) => {
  return <EmojiPickerModal onSelect={onSelect} onClose={onClose} />;
};
